import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { BookOpenIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon, HeartIcon, BrainIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const hcuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const hcuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}, {
  label: 'Doctoral',
  value: 'doctoral'
}];
interface HcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const hcuPrograms: HcuProgram[] = [
// Bachelor's - Arts
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67002'
}, {
  shortTitle: 'B.A. - Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Criminal Justice - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67003'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Business Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Business Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67004'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - General Math and Science Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - General Math and Science Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67034'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Humanities Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Humanities Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67033'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67051'
}, {
  shortTitle: 'B.A. - Psychology - Christian Counseling',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Christian Counseling - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67006'
}, {
  shortTitle: 'B.A. - Psychology - Marriage and Family Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Marriage and Family Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67007'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67008'
}, {
  shortTitle: 'B.A. - Psychology to Master of Arts in Psychology (5 Year Accelerated)',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology to Master of Arts in Psychology (5 Year Accelerated) - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67048'
}, {
  shortTitle: 'B.A. - Theological Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Theological Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67009'
},
// Bachelor's - Business Administration
{
  shortTitle: 'B.B.A. - Business Administration',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Administration - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67005'
}, {
  shortTitle: 'B.B.A. - Business Data Analytics',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Data Analytics - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67052'
}, {
  shortTitle: 'B.B.A. - Human Resource Development and Change',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Human Resource Development and Change - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67053'
}, {
  shortTitle: 'B.B.A. - International Business',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in International Business - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67031'
}, {
  shortTitle: 'B.B.A. - Management Information Systems',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management Information Systems - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67054'
}, {
  shortTitle: 'B.B.A. - Management',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67001'
}, {
  shortTitle: 'B.B.A. - Marketing',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Marketing - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67032'
},
// Master's - Arts
{
  shortTitle: 'M.A. - Apologetics',
  longTitle: 'Master of Arts (M.A.) in Apologetics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67011'
}, {
  shortTitle: 'M.A. - Christian Leadership - Practical Theology Track',
  longTitle: 'Master of Arts (M.A.) in Christian Leadership - Practical Theology Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67014'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67015'
}, {
  shortTitle: 'M.A. - Human Services - Marriage and Family Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Marriage and Family Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67016'
}, {
  shortTitle: 'M.A. - Human Services - Professional Life Coaching Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Professional Life Coaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67040'
}, {
  shortTitle: 'M.A. - Marriage and Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Marriage and Family Therapy - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67018'
}, {
  shortTitle: 'M.A. - Psychology - Applied Project Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Applied Project Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67041'
}, {
  shortTitle: 'M.A. - Psychology - Teaching Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Teaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67044'
}, {
  shortTitle: 'M.A. - Psychology - Thesis Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Thesis Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67042'
}, {
  shortTitle: 'M.A. - Theological Studies - Theological Studies Track',
  longTitle: 'Master of Arts (M.A.) in Theological Studies - Theological Studies Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67019'
},
// Master's - Business Administration
{
  shortTitle: 'M.B.A. - Professional MBA in Accounting and Finance',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Accounting and Finance - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67039'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Business Data Analytics',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Business Data Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67013'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67010'
},
// Master's - Divinity
{
  shortTitle: 'M.Div. - Biblical Languages Track',
  longTitle: 'Master of Divinity (M.Div.) in Biblical Languages Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67020'
}, {
  shortTitle: 'M.Div. - English Language Track',
  longTitle: 'Master of Divinity (M.Div.) in English Language Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67021'
},
// Master's - Education
{
  shortTitle: 'M.Ed. - Curriculum and Instruction',
  longTitle: 'Master of Education (M.Ed.) in Curriculum and Instruction - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67022'
}, {
  shortTitle: 'M.Ed. - Educational Administration',
  longTitle: 'Master of Education (M.Ed.) in Educational Administration - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67023'
}, {
  shortTitle: 'M.Ed. - Professional School Counseling',
  longTitle: 'Master of Education (M.Ed.) in Professional School Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67045'
},
// Master's - Health Administration
{
  shortTitle: 'M.H.A. - Professional Science Masters in Health Professions',
  longTitle: 'Master of Health Administration (M.H.A.) in Professional Science Masters in Health Professions - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'health',
  mProgram: '67017'
},
// Master's - Science
{
  shortTitle: 'M.S. - Artificial Intelligence',
  longTitle: 'Master of Science (M.S.) in Artificial Intelligence - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67028'
}, {
  shortTitle: 'M.S.C.J. - Criminal Justice',
  longTitle: 'Master of Science (M.S.C.J.) in Criminal Justice - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67012'
}, {
  shortTitle: 'M.S. - Computer and Information Sciences',
  longTitle: 'Master of Science (M.S.) in Computer and Information Sciences - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67036'
}, {
  shortTitle: 'M.S. - Cybersecurity',
  longTitle: 'Master of Science (M.S.) in Cybersecurity - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67043'
}, {
  shortTitle: 'M.S. - Data Science',
  longTitle: 'Master of Science (M.S.) in Data Science - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67037'
}, {
  shortTitle: 'M.S. - Human Resource Management in People Analytics',
  longTitle: 'Master of Science (M.S.) in Human Resource Management in People Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67026'
}, {
  shortTitle: 'M.S. - Kinesiology - Sports Management',
  longTitle: 'Master of Science (M.S.) in Kinesiology - Sports Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67027'
}, {
  shortTitle: 'M.S. - Leadership',
  longTitle: 'Master of Science (M.S.) in Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67035'
}, {
  shortTitle: 'M.S. - Learning, Technology, and Design',
  longTitle: 'Master of Science (M.S.) in Learning, Technology, and Design - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67025'
},
// Master's - Nursing
{
  shortTitle: 'M.S.N. - Family Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Family Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67038'
}, {
  shortTitle: 'M.S.N. - Nurse Education',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Education - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67046'
}, {
  shortTitle: 'M.S.N. - Nurse Leadership',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67047'
}, {
  shortTitle: 'M.S.N. - Pediatric Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Pediatric Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67049'
}, {
  shortTitle: 'M.S.N. - RN to MSN',
  longTitle: 'Master of Science in Nursing (M.S.N.) in RN to MSN - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67050'
},
// Doctoral
{
  shortTitle: 'Ed.D. - Executive Educational Leadership',
  longTitle: 'Doctor of Education (Ed.D.) in Executive Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67029'
}, {
  shortTitle: 'Ed.D. - Mental Health and Human Services',
  longTitle: 'Doctor of Education (Ed.D.) in Mental Health and Human Services - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67030'
}, {
  shortTitle: 'Ed.S. - Educational Leadership',
  longTitle: 'Educational Specialist (Ed.S.) in Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67024'
}];
export function HCULandingPage() {
  usePageMeta('Houston Christian University | Online Degree Programs', 'Explore flexible online degree programs at Houston Christian University. Faith-based education combining reason, liberal arts, and technical training for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
    ogType: 'website',
    // This page also answers /universities and
    // /universities/houston-christian-university; all three consolidate here.
    canonical: '/colleges/houston-christian-university'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    arts: 'Arts',
    business: 'Business Administration',
    divinity: 'Divinity',
    education: 'Education',
    health: 'Health Administration',
    science: 'Science',
    nursing: 'Nursing'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    hcuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return hcuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = hcuPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/4496hy3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Houston Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
            primaryColor: '#002F6C',
            accentColor: '#A6192E',
            backUrl: '/universities',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1873" data-mp="qeqwi5k2y3w2">
          <div data-id="element-1874" data-mp="qg230d4wtpqv">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1875" data-mp="qgljrqitq83b">
              Degree Level
            </label>
            <div className="relative" data-id="element-1876" data-mp="3p4indo6etow3">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800" data-id="element-1877" data-mp="3p3g15tk18ami">
                <option value="" data-id="element-1878" data-mp="1zmu6zmwia410">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-1879" data-mp="2ndzn836cyybu">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1880" data-mp="2ecjm6m1fo8ka">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1881" data-mp="2idbt38mu21de" />
              </div>
            </div>
          </div>

          <div data-id="element-1882" data-mp="12wuwhmwsovfs">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1883" data-mp="12wbhadyj4l5g">
              Area of Study
            </label>
            <div className="relative" data-id="element-1884" data-mp="1n75lbzqpa2y4">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1885" data-mp="1ife2ychp73j2">
                <option value="" data-id="element-1886" data-mp="1w697xdp2t0hd">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-1887" data-mp="ksov3m0bdwpa">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1888" data-mp="2sdnjbwui7qte">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1889" data-mp="2wfqz2xo7hhkh" />
              </div>
            </div>
          </div>

          <div data-id="element-1890" data-mp="31d0my8vy5l67">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1891" data-mp="31chdzho9y74b">
              Program
            </label>
            <div className="relative" data-id="element-1892" data-mp="3b5pvauw7aixb">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1893" data-mp="3g26tyj110fon">
                <option value="" data-id="element-1894" data-mp="2xzzphxqkdvxu">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-1895" data-mp="372iqsdepc0hz">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1896" data-mp="3gybynjhh75tf">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1897" data-mp="3bz94iu31ak08" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-1898" data-mp="3pqxg8w9df6go">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1899" data-mp="3kqqmbqewa8p">
          <div data-id="element-1900" data-mp="3uqjun90xqsr2">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1901" data-mp="3ur1z2sxr4da2">
              Selected Program
            </label>
            <div className="relative" data-id="element-1902" data-mp="n6ajuak65wdz">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1903" data-mp="n593proaqjpe">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-1904" data-mp="3lnwmiffid5x9">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1905" data-mp="3qkmwcl0pz5a8">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1906" data-mp="3gojwjltcbiqg" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1907" data-mp="yer5cfmq1yr" />

          <div data-id="element-1908" data-mp="xwx8mgfvzu0">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1909" data-mp="wc32xy1eiwo">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1910" data-mp="3i7bkbxcs7m9e">
              <option value="" data-id="element-1911" data-mp="34evophm7mqii">-- Please Select --</option>
              {hcuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1912" data-mp="2zh3nfwzods1n">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1913" data-mp="2qfdwes9pgv4e">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1914" data-mp="15zo30qb8huk3">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1915" data-mp="19z6p3peskboi">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1916" data-mp="1jxw51cn4nm24">
              <option value="" data-id="element-1917" data-mp="r8yqnkm1e9en">-- Please Select --</option>
              {hcuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1918" data-mp="w5p4e1adcsww">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1919" data-mp="3iun1d8um9nn">{errors.startDate}</p>}
          </div>

          <div data-id="element-1920" data-mp="8gd1fj5mxh31">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1921" data-mp="8fuvfut67ohx">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1922" data-mp="35d9odsfhsb79">
              <option value="" data-id="element-1923" data-mp="2vmeuc1w087b9">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1924" data-mp="30fhooc8lvksi">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1925" data-mp="2qnk80pytw0me">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1926" data-mp="2co2iu9hmelci">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1927" data-mp="2cqf9rc5cw61p">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1928" data-mp="1t4de1yycf9g2" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1929" data-mp="273mu8ymju3k1">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1930" data-mp="2qs41n77otqxw">
          <div data-id="element-1931" data-mp="2lqy50yawpjj3">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1932" data-mp="2lsixmidnuseb">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1933" data-mp="2ut7b9bz56qqo" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1934" data-mp="2b7c5x7s90km2">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1935" data-mp="2l3f6i97gcb6w">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1936" data-mp="2l1az3hacgu1v" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1937" data-mp="uqq1k61t6vkt">
                <div data-id="element-1938" data-mp="ur839i78q204">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1939" data-mp="ussouibkualu">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="City" data-id="element-1940" data-mp="dh44wcu3splx" />
                </div>
                <div data-id="element-1941" data-mp="x3tkp7d434ox">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1942" data-mp="x77kc7ue4zso">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="State" data-id="element-1943" data-mp="3cvzhe9dzxtke" />
                </div>
              </div>
              <div data-id="element-1944" data-mp="zmdhuo1lmy7u">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1945" data-mp="1ndslck543lko">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`} placeholder="123 Main St" data-id="element-1946" data-mp="1ecudtrvgsvxc" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1947" data-mp="9do05lb622rt">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1948" data-mp="4djny7haju7e">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1949" data-mp="3cuo6ohpf65dg">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1950" data-mp="3hu0q0sh30uba" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1951" data-mp="2u4er864mgh3t">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1952" data-mp="12ttfbszrf618">
          <div className="grid grid-cols-2 gap-3" data-id="element-1953" data-mp="12umnmyeda3lh">
            <div data-id="element-1954" data-mp="16t27wuj2cxv7">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1955" data-mp="16tlskj03m0cx">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1956" data-mp="f51tdjcrjjww" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1957" data-mp="1zhzpevfgaqmw">{errors.firstName}</p>}
            </div>
            <div data-id="element-1958" data-mp="29lo9odzc2a0f">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1959" data-mp="29m7uc2gdbci5">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1960" data-mp="1zutm96acp4de" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1961" data-mp="1gxa9psoop3m8">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1962" data-mp="1cxryxpqku8x4">
            <div data-id="element-1963" data-mp="3bbtclwd5194s">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1964" data-mp="3bccvpgeiy3ke">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1965" data-mp="3qkarow5sljta" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1966" data-mp="3lq6i05556xoi">{errors.email}</p>}
            </div>
            <div data-id="element-1967" data-mp="3bubsqr6ey4cr">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1968" data-mp="3buv0xg69hedl">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1969" data-mp="32svlgsawaicr" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1970" data-mp="168fixyxj9itm">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#002F6C] focus:ring-[#002F6C] cursor-pointer flex-shrink-0" data-id="element-1971" data-mp="1k0n937ubabjc">
            By submitting this form, I confirm that the information provided on
            this form is accurate and complete. I agree that Houston Christian
            University and its affiliates may call and/or text me about its
            offerings using automated technology. I understand consent is not a
            condition of purchasing services.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1972" data-mp="2fw7inxlhg93r">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1973" data-mp="2fx0fa5qq9i86">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1974" data-mp="2ku8ym9nm3sb0" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#A6192E] to-[#7A1222] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1975" data-mp="2yu1m7w3udser">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1976" data-mp="3hjhwvc98r18w" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1977" data-mp="51yp8d2raqxy">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1978" data-mp="4zmb6b04d0q6">
        <img src="https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png" alt="Houston Christian University Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-1979" data-mp="3r96px22qe9nn" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#002F6C] to-[#001A3D] relative overflow-hidden" data-id="element-1980" data-mp="33jvzoej0bxnr">
        {/* Explicit grid placement rather than a duplicated list: on mobile the
            form follows the headline so it lands within reach, and the selling
            points move below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-1981" data-mp="2zhr6ti0dv526">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-1982" data-mp="153ve967hpcgp">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-1983" data-mp="vbhcwekbq7v2">
              Houston Christian University
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] text-white tracking-tight" data-id="element-1984" data-mp="rbp3y0r0pzp6">
              {currentProgram ? currentProgram.longTitle : <>Online Degrees at Houston Christian University</>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-1985" data-mp="1a15bv9518ufm">
            <form onSubmit={e => e.preventDefault()} id="hcu-lead-form" data-id="element-1986" data-mp="3ut78nzbhuqld">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1987" data-mp="2f86ddhxqp45e" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1988" data-mp="2j7niubd621ak" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#002F6C] mb-4" data-id="element-1989" data-mp="2syrwn7r3vg3t">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1990" data-mp="32ysj32k9ucc8">
                <div className="bg-gradient-to-r from-[#A6192E] to-[#7A1222] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1991" data-mp="36zm7dx1mlt5n"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1992" data-mp="halp4lo63cse">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1993" data-mp="h9bphs1o1wjn">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Selling points. Placed after the form in the markup so mobile gets
              them below it; the grid puts them back under the headline on
              desktop. */}
          <ul className="space-y-2.5 max-w-lg mx-auto lg:mx-0 lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-1994" data-mp="3bxgv26xhiplb">
            {['Integrates faith and reason in academics', 'Courses available at any time of the day', '60-year academic tradition focused on quality', 'Some of the lowest tuition available today', 'SACSCOC Accredited'].map(item => <li key={item} className="flex items-start text-base lg:text-lg text-white" data-id="element-1995" data-mp="1hj1xff01i338">
                <CheckCircle2Icon className="w-5 h-5 mt-0.5 text-white/80 mr-3 flex-shrink-0" data-id="element-1996" data-mp="25a7e1wdt0zel" />
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Reasons to Choose HCU */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-1997" data-mp="1wabxtd5e6e7p">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1998" data-mp="215zlfo486zb3">
          <div className="mb-10" data-id="element-1999" data-mp="ksj7q622ck7o">
            <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-2000" data-mp="ks9iikoueh31"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C]" data-id="element-2001" data-mp="prvqofptd74z">
              Reasons to Choose HCU
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2002" data-mp="2rgtbpm3i5hxq">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2003" data-mp="2mhyhzp3zy8yu">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2004" data-mp="2hr9tminm5s3x">
                <HeartIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2005" data-mp="2dqg5bo69ebai" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2006" data-mp="28qtnf1fhk6vd">
                Faith-Based Education
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2007" data-mp="23rh2wnc37bvy">
                Integrate Christian values with academic excellence in every
                program.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2008" data-mp="1us3ttvrvkpbr">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2009" data-mp="3t76qqu5lvic8">
                <BrainIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2010" data-mp="3p4tpahxw66l9" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2011" data-mp="1axkf4lklf01p">
                Liberal Arts Core
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2012" data-mp="1fvdg9071h3v4">
                Develop critical thinking through faith, reason, and liberal
                arts curriculum.
              </p>
            </div>
            <div data-id="element-2013" data-mp="1onfgaxd74i4v">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2014" data-mp="1onxieamjhqkp">
                <ClockIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2015" data-mp="s0j6mqrtyht0" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2016" data-mp="wy2ri9j19stx">
                Flexible Online Learning
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2017" data-mp="111h8b2esqd8f">
                Study on your schedule with fully online degree programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose HCU */}
      <section className="py-16 bg-gray-50" data-id="element-2018" data-mp="2hcct4kyn1mq8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2019" data-mp="2hf7huoooptpg">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-2020" data-mp="1t1c87nrejoos">
            <div className="flex-1" data-id="element-2021" data-mp="1o2ixg33i2bsb">
              <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-2022" data-mp="1o3khgx2hu8m6"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-8" data-id="element-2023" data-mp="21u632tjc0wnr">
                Why Choose <span className="text-[#002F6C]" data-id="element-2024" data-mp="1x25ukcp74vfk">HCU</span>
              </h2>
              <ul className="space-y-4" data-id="element-2025" data-mp="2br24kfatciit">
                <li className="flex items-start" data-id="element-2026" data-mp="2bq0twbxply1m">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2027" data-mp="26shdp6hgbb71" />
                  <span className="text-gray-700" data-id="element-2028" data-mp="2krqhf6oh0cu4">
                    <strong data-id="element-2029" data-mp="2krgq9frovzmu">Faith-integrated curriculum</strong> combining
                    Christian teachings with academic rigor
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2030" data-mp="qf0nwq20l5n9">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2031" data-mp="qfsibxjvkxby" />
                  <span className="text-gray-700" data-id="element-2032" data-mp="meqjvixs8pb1">
                    <strong data-id="element-2033" data-mp="c9i81dmsn8gf">Liberal arts foundation</strong> that develops
                    critical thinking and ethical leadership
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2034" data-mp="2j5q7t6d5tdz">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2035" data-mp="2idv0jgohxo8" />
                  <span className="text-gray-700" data-id="element-2036" data-mp="7fw1823b9ysj">
                    <strong data-id="element-2037" data-mp="3opkqdyxz3itz">Fully online programs</strong> designed for working
                    adults and busy schedules
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2038" data-mp="3fp5s7yzwu992">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2039" data-mp="3fqh5qqj5qvdd" />
                  <span className="text-gray-700" data-id="element-2040" data-mp="3kp1xs2o9a8qq">
                    <strong data-id="element-2041" data-mp="1dxsym63e7nsb">Expert faculty</strong> with real-world experience
                    and commitment to student success
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2042" data-mp="1iut5gj82gukh">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2043" data-mp="2kpbfwevv0w7p" />
                  <span className="text-gray-700" data-id="element-2044" data-mp="2pmtrkd19hpje">
                    <strong data-id="element-2045" data-mp="2pliibxp1rpkw">Career-focused education</strong> preparing you for
                    leadership in your field and community
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2046" data-mp="2zer17fcmg9g0">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2047" data-mp="33fjdkhgsiugi" />
                  <span className="text-gray-700" data-id="element-2048" data-mp="38dl57qsa823l">
                    <strong data-id="element-2049" data-mp="38c9tn4slhq1x">Supportive Christian community</strong> of students,
                    faculty, and staff
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-2050" data-mp="3ibiri8qkzotk">
              <img src="https://cdn.magicpatterns.com/uploads/jUSjuibhb7LUe6o3g91xYz/ChatGPT_Image_Jul_1,_2026,_01_59_51_AM.png" alt="Student studying at Houston Christian University" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-2051" data-mp="1izy7s53tvqsk" />
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-2052" data-mp="3sha9yylrs7mw">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2053" data-mp="3sduuhuo6owky">
          <div className="w-12 h-1 bg-[#002F6C] mx-auto mb-6" data-id="element-2054" data-mp="3nip7stmyjdvt"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-6" data-id="element-2055" data-mp="o2aft8p8g2wm">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2056" data-mp="k176l51ox54d">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges (SACSCOC)
            to award associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-2057" data-mp="2zpgwhex3i09e">
            Our accreditation ensures that you receive a{' '}
            <strong data-id="element-2058" data-mp="2w9mdn6a5m0c1">quality education</strong> that is recognized by employers
            and other institutions. Whether you're{' '}
            <strong data-id="element-2059" data-mp="317nx3sbhs1o1">starting your college journey</strong> or{' '}
            <strong data-id="element-2060" data-mp="2n8xzmbkrfzz5">advancing your career</strong>, you can trust that your HCU
            degree represents excellence.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#002F6C] py-16" data-id="element-2061" data-mp="2s9vhso5rsu0n">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2062" data-mp="2di3amzg4qma0">
          <div className="w-12 h-1 bg-white/40 mx-auto mb-6" data-id="element-2063" data-mp="2iaedob662o6s"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-2064" data-mp="2ibph29vp9txd">
            Ready to Start Your Journey at{' '}
            <span className="text-white" data-id="element-2065" data-mp="28js16ptqydsb">HCU?</span>
          </h2>
          <div className="mt-8" data-id="element-2066" data-mp="28hyw3eys81mf">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#002F6C] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-2067" data-mp="1ukjxbjmn2hhs">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-2068" data-mp="1b0bcjxx9ur9y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-2069" data-mp="1px2f2g28sj3s">
          <p data-id="element-2070" data-mp="1kvmxixz9wuq5">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges to award
            associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p data-id="element-2071" data-mp="1tvi74yd6kho2">
            For more information about HCU's online programs, visit{' '}
            <a href="https://hc.edu/online-learning/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#002F6C]" data-id="element-2072" data-mp="28obt92myjw1p">
              hc.edu/online-learning
            </a>
            .
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-2073" data-mp="2dn6fyfo1q4r3">
            © {new Date().getFullYear()} Houston Christian University. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}